import { create } from 'zustand';
import { getUserDefaults, setUserDefault } from '../../services/userdefaults/userDefaultService';
import { tourCompletedKey } from './constants';
import { getTour, getTourForPath, listTours, tourOwnsPath } from './tours';
import type { OnboardingStep } from './types';

export type OnboardingTourStatus = 'skipped' | 'completed';

interface OnboardingState {
  userId: string | null;
  impersonating: boolean;
  tourId: string | null;
  sessionId: number;
  stepIndex: number;
  running: boolean;
  hydrated: boolean;
  hydrating: boolean;
  hydrationError: boolean;
  saveError: boolean;
  /** Both skipped and completed suppress automatic replay, independently per page. */
  completedTours: Record<string, boolean>;
  tourStatuses: Record<string, OnboardingTourStatus>;
  pendingStatuses: Record<string, OnboardingTourStatus>;
  branchSteps: OnboardingStep[] | null;
  completedStepIds: string[];
  startTour: (tourId: string) => void;
  startBranch: (branchId: string) => void;
  next: () => void;
  back: () => void;
  skip: () => void;
  restart: () => void;
  complete: () => Promise<void>;
  dismiss: () => void;
  hydrate: (userId: string, impersonating?: boolean) => Promise<void>;
  reset: () => void;
  retryPersistence: () => Promise<void>;
  maybeStartForPath: (pathname: string) => void;
}

function currentSteps(tourId: string | null, branchSteps: OnboardingStep[] | null): OnboardingStep[] {
  if (branchSteps?.length) return branchSteps;
  return tourId ? getTour(tourId)?.steps ?? [] : [];
}

const initialState = () => ({
  userId: null as string | null,
  impersonating: false,
  tourId: null as string | null,
  sessionId: 0,
  stepIndex: 0,
  running: false,
  hydrated: false,
  hydrating: false,
  hydrationError: false,
  saveError: false,
  completedTours: {} as Record<string, boolean>,
  tourStatuses: {} as Record<string, OnboardingTourStatus>,
  pendingStatuses: {} as Record<string, OnboardingTourStatus>,
  branchSteps: null as OnboardingStep[] | null,
  completedStepIds: [] as string[],
});

export const useOnboardingStore = create<OnboardingState>((set, get) => {
  // A response belonging to a previous login must never hydrate or reopen this login.
  let generation = 0;
  let hydration: Promise<void> | null = null;
  const writes = new Map<string, Promise<void>>();

  const persist = async (tourId: string, status: OnboardingTourStatus): Promise<void> => {
    const { userId, impersonating } = get();
    const tour = getTour(tourId);
    if (!userId || impersonating || !tour) return;
    const requestGeneration = generation;
    const key = tourCompletedKey(tourId, tour.version ?? 1);
    const previous = writes.get(key);
    // Serialize a skip followed by replay completion so the older request cannot win.
    const write = (async () => {
      if (previous) await previous;
      if (requestGeneration !== generation) return;
      try {
        await setUserDefault(key, status, userId);
        if (requestGeneration !== generation) return;
        if (get().pendingStatuses[tourId] === status) {
          const pendingStatuses = { ...get().pendingStatuses };
          delete pendingStatuses[tourId];
          set({ pendingStatuses, saveError: get().saveError && Object.keys(pendingStatuses).length > 0 });
        }
      } catch (error) {
        if (requestGeneration !== generation) return;
        console.error('Failed to save onboarding progress', error);
        set({ saveError: true });
      }
    })();
    writes.set(key, write);
    await write;
    if (writes.get(key) === write) writes.delete(key);
  };

  const finish = async (requestedStatus: OnboardingTourStatus): Promise<void> => {
    const state = get();
    if (!state.running || !state.tourId) return;
    const tourId = state.tourId;
    // A later replay skip does not downgrade an already completed tour.
    const status = state.tourStatuses[tourId] === 'completed' ? 'completed' : requestedStatus;
    const needsWrite = !state.impersonating && state.tourStatuses[tourId] !== status;
    set({
      running: false,
      tourId: null,
      stepIndex: 0,
      branchSteps: null,
      completedTours: { ...state.completedTours, [tourId]: true },
      tourStatuses: { ...state.tourStatuses, [tourId]: status },
      pendingStatuses: needsWrite ? { ...state.pendingStatuses, [tourId]: status } : state.pendingStatuses,
    });
    if (needsWrite) await persist(tourId, status);
  };

  return {
    ...initialState(),
    startTour: tourId => {
      const tour = getTour(tourId);
      if (!get().userId || !tour?.steps.length) return;
      set({ tourId, sessionId: get().sessionId + 1, stepIndex: 0, running: true, branchSteps: null, completedStepIds: [] });
    },
    startBranch: branchId => {
      const { tourId, running } = get();
      const branch = tourId ? getTour(tourId)?.branches?.[branchId] : undefined;
      if (!running || !branch?.length) return;
      set({ branchSteps: branch, sessionId: get().sessionId + 1, stepIndex: 0, running: true, completedStepIds: [] });
    },
    next: () => {
      const { tourId, stepIndex, branchSteps, running } = get();
      if (!running) return;
      const steps = currentSteps(tourId, branchSteps);
      let nextIndex = stepIndex + 1;
      while (nextIndex < steps.length && steps[nextIndex].isAvailable?.() === false) nextIndex += 1;
      if (nextIndex >= steps.length) {
        void get().complete();
      } else {
        set({ stepIndex: nextIndex, completedStepIds: Array.from(new Set([...get().completedStepIds, steps[stepIndex].id])) });
      }
    },
    back: () => {
      const { tourId, stepIndex, branchSteps, running } = get();
      if (!running) return;
      const steps = currentSteps(tourId, branchSteps);
      let previousIndex = stepIndex - 1;
      while (previousIndex >= 0 && steps[previousIndex].isAvailable?.() === false) previousIndex -= 1;
      if (previousIndex >= 0) set({ stepIndex: previousIndex });
      else if (branchSteps && tourId) {
        const chooserIndex = getTour(tourId)?.steps.findIndex(step => step.layout === 'chooser') ?? -1;
        if (chooserIndex >= 0) set({ branchSteps: null, stepIndex: chooserIndex, sessionId: get().sessionId + 1, completedStepIds: [] });
      }
    },
    skip: () => { void finish('skipped'); },
    complete: () => finish('completed'),
    restart: () => {
      if (get().tourId) set({ stepIndex: 0, sessionId: get().sessionId + 1, running: true, completedStepIds: [] });
    },
    dismiss: () => set({ running: false, tourId: null, stepIndex: 0, branchSteps: null }),
    reset: () => {
      generation += 1;
      hydration = null;
      writes.clear();
      set(initialState());
    },
    hydrate: async (userId, impersonating = false) => {
      if (get().userId !== userId || get().impersonating !== impersonating) {
        get().reset();
        set({ userId, impersonating });
      }
      if (get().hydrated) return;
      if (hydration) return hydration;
      const requestGeneration = generation;
      const tours = listTours();
      set({ hydrating: true, hydrationError: false });
      hydration = (async () => {
        try {
          const map = await getUserDefaults(tours.map(tour => tourCompletedKey(tour.id, tour.version ?? 1)));
          if (requestGeneration !== generation) return;
          const tourStatuses: Record<string, OnboardingTourStatus> = { ...get().tourStatuses };
          const completedTours: Record<string, boolean> = { ...get().completedTours };
          for (const tour of tours) {
            const value = map[tourCompletedKey(tour.id, tour.version ?? 1)];
            if (value === 'completed' || value === 'skipped') {
              if (tourStatuses[tour.id] !== 'completed') tourStatuses[tour.id] = value;
              completedTours[tour.id] = true;
            }
          }
          set({ tourStatuses, completedTours, hydrated: true, hydrating: false, hydrationError: false });
        } catch (error) {
          if (requestGeneration !== generation) return;
          console.error('Failed to load onboarding progress', error);
          // A failed fetch is unknown, never an invitation to show every tour again.
          set({ hydrated: false, hydrating: false, hydrationError: true });
        } finally {
          if (requestGeneration === generation) hydration = null;
        }
      })();
      return hydration;
    },
    retryPersistence: async () => {
      await Promise.all(Object.entries(get().pendingStatuses).map(([tourId, status]) => persist(tourId, status)));
    },
    maybeStartForPath: pathname => {
      const state = get();
      if (!state.userId || !state.hydrated || state.impersonating) return;
      if (state.running && state.tourId) {
        const current = getTour(state.tourId);
        if (current && tourOwnsPath(current, pathname)) return;
        get().dismiss();
      }
      const tour = getTourForPath(pathname);
      if (tour && !get().completedTours[tour.id]) get().startTour(tour.id);
    },
  };
});

export function startOnboardingTour(tourId: string): void {
  useOnboardingStore.getState().startTour(tourId);
}

export function getCurrentStep(): OnboardingStep | null {
  const { tourId, stepIndex, running, branchSteps } = useOnboardingStore.getState();
  return running && tourId ? currentSteps(tourId, branchSteps)[stepIndex] ?? null : null;
}
